import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CalendarClock, Check, ChevronRight, ClipboardList, Lock } from 'lucide-react';
import StudentShell from './components/StudentShell';
import { fetchMyHomework } from './data/api';
import { isOpenAssessment, leadWith } from './data/assessmentOrder';
import { isAssessment } from '../TeacherDashboardV3/data/assessments';
import { shortDue, dueEndMs } from '../TeacherDashboardV3/data/unitPlan';
import { useAuth } from '../Accounts/AuthContext';
import { CubeLoader } from '../pages/LoadingSpinnerPage';
import './sdv3.css';

/* The homework page (Sandy 2026-08-07): every practice set a teacher assigned,
   on its own — due date, who assigned it, how many questions, my progress.
   To-do sets group by urgency (overdue first, then due within 3 days, then
   later); finished sets sit on the review side, where a click opens the
   set's review (the player shows the review screen once a set is done).
   Closed sets (2026-10-08): a set the teacher finished, or that closed at its
   due date, stays here as review-only when the student had started it. */

const CARD = 'bg-white border border-neutral-200 rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,.04)]';
const SOON_MS = 3 * 24 * 60 * 60 * 1000;

// "Ms. Rivera · Period 3" / "Ms. Rivera" / "Period 3" / null — whatever exists.
function assignerOf(hw) {
  return [hw.teacherName, hw.period || hw.className].filter(Boolean).join(' · ') || null;
}

function HwRow({ hw, urgency }) {
  const assigner = assignerOf(hw);
  const closed = !!hw.closed && !hw.done;
  // An assessment owes no walkthrough (2026-10-26): the player closes any the server marks.
  const walksOwed = !hw.done && !closed && hw.walksLeft > 0 && !isAssessment(hw);
  // Review status. On phones it sits under the details: beside them it took
  // ~115px and squeezed the title to ~96px (2026-10-08 audit at 375 wide).
  const status = hw.done ? (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-success-600">
      <Check size={14} strokeWidth={3} /> Done · review
    </span>
  ) : closed ? (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-neutral-500">
      <Lock size={13} strokeWidth={2.5} /> Closed · review
    </span>
  ) : null;
  return (
    <Link
      to={`/student-homework/${hw.classID}/${hw.hwId}`}
      className={CARD + ' sdv3-grow group flex items-center gap-4 p-4 no-underline text-ink hover:border-neutral-300'}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-extrabold leading-snug group-hover:text-primary-700 transition-colors">
          {hw.title}
        </span>
        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12.5px] font-semibold text-neutral-500">
          {/* Curriculum assessments (2026-10-18) read apart from practice sets. */}
          {isAssessment(hw) && <span className="font-bold text-ink">Assessment</span>}
          {assigner && <span>Assigned by {assigner}</span>}
          <span>
            <span className="font-bold text-ink tabular-nums">{hw.count}</span> questions
          </span>
          {/* Every question answered but a missed question's walkthrough is owed
              (2026-10-09): say only what is left, so the row keeps the due date on
              its line ("N of N answered" is implied). */}
          {!hw.done && hw.answered > 0 && !(walksOwed && hw.answered >= hw.count) && (
            <span className={'font-bold tabular-nums ' + (closed ? 'text-neutral-600' : 'text-primary-700')}>{hw.answered} of {hw.count} answered</span>
          )}
          {walksOwed && (
            <span className="font-bold text-amber-700">{hw.walksLeft} {hw.walksLeft === 1 ? 'walkthrough' : 'walkthroughs'} left</span>
          )}
          {closed && hw.answered > 0 && (
            <span className="font-bold text-ink tabular-nums">{hw.correct || 0} right</span>
          )}
          {hw.dueDate && !closed && (
            <span className={'inline-flex items-center gap-1' + (urgency === 'overdue' ? ' text-error-600 font-bold' : '')}>
              <CalendarClock size={13} strokeWidth={2.5} className="flex-none" />
              {urgency === 'overdue' ? 'Was due ' : ''}{shortDue(hw.dueDate)}{hw.closeAtDue && urgency !== 'overdue' ? ' · closes then' : ''}
            </span>
          )}
        </span>
        {status && <span className="mt-1.5 flex sm:hidden">{status}</span>}
      </span>
      {status && <span className="hidden sm:inline-flex flex-none">{status}</span>}
      <ChevronRight size={18} strokeWidth={2.5} className="flex-none text-neutral-300 group-hover:text-primary-500 transition-colors" />
    </Link>
  );
}

function Group({ label, tone, items, urgency }) {
  if (!items.length) return null;
  return (
    <div className="mb-6">
      <div className={'text-[11px] font-bold uppercase tracking-wide mb-2 ' + (tone || 'text-neutral-500')}>
        {label}
      </div>
      <div className="space-y-3">
        {items.map((hw) => <HwRow key={`${hw.classID}-${hw.hwId}`} hw={hw} urgency={urgency} />)}
      </div>
    </div>
  );
}

export default function HomeworkListPage() {
  const { user, handleAPIResponse } = useAuth();
  const [items, setItems] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!user) { setItems([]); return undefined; }
    fetchMyHomework({ user, handleAPIResponse })
      .then((r) => { if (alive) setItems(r); })
      .catch(() => { if (alive) { setItems([]); setFailed(true); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user && items === null) {
    return (
      <StudentShell>
        <div className="flex items-center justify-center min-h-[70vh]"><CubeLoader size={140} /></div>
      </StudentShell>
    );
  }

  const now = Date.now();
  const open = (items || []).filter((h) => !h.done && !h.closed);
  const closed = (items || []).filter((h) => !h.done && h.closed);
  const done = (items || []).filter((h) => h.done);
  const endOf = (h) => (h.dueDate ? dueEndMs(h.dueDate) : Infinity);
  // Open assessments lead their group (2026-10-18): the API sorts by the raw due
  // string, so today's in-class one (a UTC datetime) landed under sets due
  // tomorrow. Everything else keeps the API's order.
  const lead = (list) => leadWith(list, isOpenAssessment, endOf);
  const overdue = lead(open.filter((h) => endOf(h) < now));
  const dueSoon = lead(open.filter((h) => endOf(h) >= now && endOf(h) - now <= SOON_MS));
  const dueLater = lead(open.filter((h) => endOf(h) - now > SOON_MS));

  return (
    <StudentShell>
      <div className="px-6 pt-8 pb-12 max-w-[860px] w-full mx-auto">
        <Link
          to="/student-dashboard"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-neutral-500 hover:text-ink no-underline mb-4"
        >
          <ArrowLeft size={15} strokeWidth={2.5} /> Home
        </Link>
        <div className="flex items-center gap-3 mb-1">
          <ClipboardList size={26} strokeWidth={2.25} className="text-primary-500" />
          <h1 className="m-0 text-[26px] font-extrabold tracking-tight text-ink">Homework</h1>
        </div>
        {/* mt-0, not m-0: Bootstrap's .m-0 is !important and erased mb-7 (0px gap). */}
        <p className="mt-0 mb-7 text-[14px] text-neutral-500">
          Practice sets your teachers assigned. Finish a set and it moves down to Done, where you can review every question.
        </p>

        {failed && (
          <div className={CARD + ' p-5 mb-6 text-[14px] text-neutral-500'}>
            Couldn't load your homework right now. Refresh to try again.
          </div>
        )}
        {!failed && !open.length && !closed.length && !done.length && (
          <div className={CARD + ' p-6 text-[14px] text-neutral-500'}>
            Nothing assigned right now. When a teacher assigns a practice set, it shows up here.
          </div>
        )}

        <Group label="Overdue" tone="text-error-600" items={overdue} urgency="overdue" />
        <Group label="Due soon" tone="text-gold-700" items={dueSoon} />
        <Group label="Due later" items={dueLater} />

        {(closed.length > 0 || done.length > 0) && (
          <div className="mt-9 pt-6 border-t border-neutral-200">
            <Group label="Closed · tap to review" items={closed} />
            <Group label="Done · tap to review" tone="text-success-600" items={done} />
          </div>
        )}
      </div>
    </StudentShell>
  );
}
